import React, { memo, useState, useMemo, useEffect, useContext } from 'react'
import { useDispatch } from 'react-redux'
import AddIcon from 'transfix-icons/build/AddIcon'
import DeleteIcon from 'transfix-icons/build/DeleteIcon'
import { Button } from 'transfix-ui/components/Button'
import { Card } from 'transfix-ui/components/Card'
import { IconButton } from 'transfix-ui/components/IconButton'
import { MapMarker } from 'transfix-ui/components/MapMarker'
import { ConfirmationModal } from 'transfix-ui/components/Modals'
import { OverflowMenu, useOverflowToggle } from 'transfix-ui/components/OverflowMenu'
import { Tag, TAG_SIZE, TAG_STYLE } from 'transfix-ui/components/Tag'
import { IFullStop } from 'types'
import { STOP_TYPE as ALT_STOP_TYPE } from 'types/shipment'
import { useDebouncedCallback } from 'use-debounce'
import { FeatureFlagsContext } from 'utils/context/FeatureFlagsContext'
import { useMarketplaceContext } from 'utils/context/MarketplaceContext'
import uuid from 'uuid'
import { InlineRow } from 'apps/fleet-planner/components/load-details/InlineRow'
import { LOAD_STATE, STOP_TYPE } from 'apps/fleet-planner/constants/loads'
import { UserContext } from 'apps/fleet-planner/context'
import { ILoad, IStop } from 'apps/fleet-planner/types/loads'
import { IExternalPermissions } from 'apps/fleet-planner/types/permissions'
import { deleteStop, fetchLoadDetails } from 'apps/fleet-planner/utils/actions/loadDetails'
import { IDropdownOption } from 'components/forms'
import { EllipsisIcon } from 'transfix-icons'
import { capitalize } from 'utils/capitalize'
import { AppointmentInlineEdit } from './AppointmentInlineEdit'
import { DetailsInlineEdit } from './DetailsInlineEdit'
import { DropTrailerTimesInlineEdit } from './DropTrailerTimesInlineEdit'
import { ExternalInstructionsDisplay } from './ExternalInstructionsDisplay'
import { FacilityInlineEdit } from './FacilityInlineEdit'
import { InOutTimesInlineEdit } from './InOutTimesInlineEdit'
import { InstructionInlineEdit } from './InstructionInlineEdit'
import { StopCreate } from './StopCreate'

export const isStop = (
  stop: IStop,
  pickupLength: number,
  dropoffLength: number,
  position: string
): boolean => {
  const positionCheck =
    position === 'first' ? [1, pickupLength + 1] : [pickupLength, pickupLength + dropoffLength]
  if (stop.stopType === STOP_TYPE.pickup && stop.stopSequence === positionCheck[0]) {
    return true
  }
  if (stop.stopSequence === positionCheck[1]) {
    return true
  }
  return false
}

interface IStopHeaderProps {
  stopType: IStop['stopType'] | ALT_STOP_TYPE
  sequence: number
  onStopDelete?: () => void
  showOverflowMenu?: boolean
  showDropTrailer?: boolean
}

export const StopHeader = memo<IStopHeaderProps>(
  ({ onStopDelete, sequence, showDropTrailer = false, showOverflowMenu = false, stopType }) => {
    const { clickOverflowTrigger, expanded, setExpanded } = useOverflowToggle()
    const options: IDropdownOption[] = [
      {
        label: 'Delete Stop',
        itemComponent: (
          <div className='flex flex-row items-center'>
            <DeleteIcon size='large' />
            <span className='ml2'>Delete Stop</span>
          </div>
        ),
        action: () => {
          if (onStopDelete) onStopDelete()
        },
      },
    ]
    return (
      <div className='flex justify-between items-center mt2 mb4'>
        <div className='flex items-center'>
          <MapMarker type={stopType} sequence={sequence} dataTest={`${stopType}-${sequence}`} />
          <span className='body-medium pl2 pr4'>
            {capitalize(stopType)} {sequence > 0 ? sequence : ''}
          </span>
          {showDropTrailer && (
            <Tag
              label='Drop Trailer'
              size={TAG_SIZE.xsmall}
              tagStyle={TAG_STYLE.info}
              outlineOnly
            />
          )}
        </div>
        {showOverflowMenu && (
          <OverflowMenu
            options={options}
            trigger={
              <IconButton
                data-test='dropdown'
                label='overflow-trigger'
                Icon={EllipsisIcon}
                onClick={clickOverflowTrigger}
                size='large'
              />
            }
            expanded={expanded}
            dropdownId='stop-actions'
            onExpanded={setExpanded}
            alignment='right'
            className='ml4 mr4-l'
          />
        )}
      </div>
    )
  }
)

interface IStopInlineEditProps {
  status: LOAD_STATE
  marketplaceStop?: IFullStop
  stop: IStop
  stops: IStop[]
  sequence: number
  currentEditField?: string
  onEditModeRequested(name?: string): void
  updateLoad(changes: Partial<ILoad>): void
  handleStopChange: (stops: IStop[]) => void
  onCancel: () => void
  error?: string
  loading?: boolean
  loadId?: string
  permissions?: IExternalPermissions
  isExternal?: boolean
}

export const StopInlineEditContainer = memo<IStopInlineEditProps>(
  ({
    currentEditField,
    error,
    handleStopChange,
    isExternal,
    loadId = '',
    loading,
    marketplaceStop,
    onCancel,
    onEditModeRequested,
    permissions,
    sequence,
    status,
    stop,
    stops,
    updateLoad,
  }) => {
    // Time format settings set in /fleet-planner/user/settings
    const { timeFormats, userData } = useContext(UserContext)
    const hasDropTrailerEnabled = userData?.user?.hasDropTrailerEnabled || false
    const { id = '', isDropTrailer, stopSequence, stopType } = stop || {}
    const pickupLength = useMemo(
      () => stops.filter(updatedStop => updatedStop.stopType === STOP_TYPE.pickup).length,
      [stops]
    )
    const dropoffLength = useMemo(
      () => stops.filter(updatedStop => updatedStop.stopType === STOP_TYPE.dropoff).length,
      [stops]
    )
    const isNotFirstStop = useMemo(() => !isStop(stop, pickupLength, dropoffLength, 'first'), [
      stops,
    ])
    const { getFlag } = useContext(FeatureFlagsContext)
    const { enabled: dropTrailerTimestampsEnabled } = getFlag('dropTrailerTimestamps')
    const isLastStop = useMemo(() => isStop(stop, pickupLength, dropoffLength, 'last'), [stops])
    const [debouncedChange, setDebouncedChange] = useState<Partial<IStop>>({})
    useEffect(() => setDebouncedChange(stop), [stop])
    const [showDeleteStopModal, setShowDeleteStopModal] = useState<boolean>(false)
    const stopIndex = stops.indexOf(stop)
    const dispatch = useDispatch()

    const updateStopChangeHandler = (s: Partial<IStop>) => {
      const stopId = s.id
      const updatedStops = stops.map(currentStop => {
        const currentStopStopId = currentStop.id
        if (currentStopStopId === stopId) {
          return { ...currentStop, ...s }
        }
        return currentStop
      })
      handleStopChange(updatedStops)
    }

    const [debouncedOnChange] = useDebouncedCallback(() => {
      updateStopChangeHandler(debouncedChange)
    }, 500)

    const onSubmitHandler = () => {
      try {
        updateLoad({ stops })
      } catch (err) {
        dispatch(fetchLoadDetails(loadId))
      }
    }

    const updateStopAttributes = (attributes: Partial<IStop>) => {
      const newStop = Object.assign(debouncedChange, attributes)
      setDebouncedChange({ ...newStop })
      debouncedOnChange()
      return newStop
    }

    const closeEditContainerHandler = () => setShowDeleteStopModal(false)
    const openEditContainerHandler = () => setShowDeleteStopModal(true)
    const deleteStopHandler = () => {
      dispatch(deleteStop(id, loadId))
      closeEditContainerHandler()
    }

    const calculateStopSequences = (updatedStops: IStop[]) => {
      const updatedPickups = updatedStops
        .filter(updatedStop => updatedStop.stopType === STOP_TYPE.pickup)
        .map((pickupStop, idx) => {
          pickupStop.stopSequence = idx + 1
          return pickupStop
        })
      const updatedDropoffs = updatedStops
        .filter(updatedStop => updatedStop.stopType === STOP_TYPE.dropoff)
        .map((dropoffStop, idx) => {
          dropoffStop.stopSequence = updatedPickups.length + (idx + 1)
          return dropoffStop
        })
      const newStops = [...updatedPickups, ...updatedDropoffs]
      handleStopChange(newStops)
    }

    const addStopHandler = () => {
      const updatedStops = [...stops, { stopType, uuid: uuid() }] as IStop[]
      calculateStopSequences(updatedStops)
    }

    const firstPickup = useMemo(
      () =>
        stops
          .filter((s: IStop) => s.stopType === 'pickup')
          .sort((a: IStop, b: IStop) => a.stopSequence - b.stopSequence)[0],
      [stops]
    )

    const stopTypeTitle = capitalize(stopType)
    const showOverflowMenu = !!(isNotFirstStop && id && !isExternal)

    const isInOutTimesVisible = isExternal ? status !== LOAD_STATE['Ready for Dispatch'] : true
    const { isLoading: mpLoading } = useMarketplaceContext()

    return (
      <>
        <Card className='mt4 mb4'>
          <StopHeader
            showDropTrailer={isExternal && stop?.isDropTrailer}
            showOverflowMenu={showOverflowMenu}
            stopType={stopType}
            sequence={sequence}
            onStopDelete={openEditContainerHandler}
          />
          {stop.id && (
            <>
              <InlineRow
                rowTitle={`${stopTypeTitle} Location`}
                rowBody={
                  <FacilityInlineEdit
                    isLocked={permissions?.facility}
                    loading={loading}
                    error={error}
                    stop={stop}
                    currentEditField={currentEditField}
                    onEditModeRequested={onEditModeRequested}
                    onSubmit={onSubmitHandler}
                    onCancel={onCancel}
                    onStopUpdate={updateStopAttributes}
                    sequence={sequence}
                  />
                }
              />
              <InlineRow
                rowTitle='Appointment'
                rowBody={
                  <AppointmentInlineEdit
                    isLocked={permissions?.appointment}
                    timeFormats={timeFormats}
                    loading={loading}
                    error={error}
                    stopIndex={stopIndex}
                    stop={stop}
                    currentEditField={currentEditField}
                    onEditModeRequested={onEditModeRequested}
                    onSubmit={onSubmitHandler}
                    onCancel={onCancel}
                    onStopUpdate={updateStopAttributes}
                  />
                }
              />
              {isInOutTimesVisible && (
                <InlineRow
                  bodyClass='pt1'
                  containerClass='load-details-row-in-out-container'
                  rowTitle='Check In & Check Out'
                  rowBody={
                    <InOutTimesInlineEdit
                      marketplaceStop={marketplaceStop}
                      timeFormats={timeFormats}
                      loading={loading}
                      error={error}
                      stop={stop}
                      currentEditField={currentEditField}
                      onEditModeRequested={onEditModeRequested}
                      onSubmit={onSubmitHandler}
                      onCancel={onCancel}
                      onStopUpdate={updateStopAttributes}
                    />
                  }
                />
              )}
              {dropTrailerTimestampsEnabled && hasDropTrailerEnabled && isDropTrailer && (
                <InlineRow
                  bodyClass='pt1'
                  containerClass='load-details-row-in-out-container'
                  rowTitle={`Empty Trailer ${stop.stopType === 'dropoff' ? 'Ready' : 'Arrival'}`}
                  rowBody={
                    <DropTrailerTimesInlineEdit
                      timeFormats={timeFormats}
                      loading={loading}
                      error={error}
                      stop={stop}
                      currentEditField={currentEditField}
                      onEditModeRequested={onEditModeRequested}
                      onSubmit={onSubmitHandler}
                      onCancel={onCancel}
                      onStopUpdate={updateStopAttributes}
                    />
                  }
                />
              )}
              <InlineRow
                rowTitle={`${stopTypeTitle} Details`}
                rowBody={
                  <DetailsInlineEdit
                    isExternal={isExternal}
                    isLocked={permissions?.details}
                    firstPickup={firstPickup}
                    loading={loading}
                    error={error}
                    stop={stop}
                    currentEditField={currentEditField}
                    onEditModeRequested={onEditModeRequested}
                    onSubmit={onSubmitHandler}
                    onCancel={onCancel}
                    onStopUpdate={updateStopAttributes}
                  />
                }
              />
              {isExternal && (
                <ExternalInstructionsDisplay
                  isLoading={mpLoading}
                  name={`Customer ${stopTypeTitle} Instructions`}
                  value={stop?.instructionsFromCustomer}
                />
              )}
              <InlineRow
                rowTitle={`Internal ${stopTypeTitle} Instructions`}
                rowBody={
                  <InstructionInlineEdit
                    loading={loading}
                    error={error}
                    stop={stop}
                    currentEditField={currentEditField}
                    onEditModeRequested={onEditModeRequested}
                    onSubmit={onSubmitHandler}
                    onCancel={onCancel}
                    onStopUpdate={updateStopAttributes}
                    sequence={sequence}
                  />
                }
              />
            </>
          )}
          {!stop.id && (
            <StopCreate
              timeFormat={timeFormats.stopTimeInput}
              stopIndex={stopIndex}
              loading={loading}
              error={error}
              stops={stops}
              updateLoad={updateLoad}
              stop={stop}
              onEditModeRequested={onEditModeRequested}
              currentEditField={currentEditField}
              onCancel={onCancel}
            />
          )}
        </Card>
        {isLastStop && id && !isExternal && (
          <Button
            onClick={addStopHandler}
            startIcon={<AddIcon />}
            color='tertiary'>{`Add ${stopType} stop`}</Button>
        )}
        <ConfirmationModal
          title='Delete Stop?'
          size={3}
          open={showDeleteStopModal}
          actions={[
            {
              label: 'Cancel',
              type: 'dismiss',
            },
            {
              label: 'Delete Stop',
              type: 'confirm',
              dataTest: `confirm-delete-stop-${stopType}-${stopSequence}`,
              action: deleteStopHandler,
            },
          ]}
          onClose={closeEditContainerHandler}
          dataTest='delete-load'
          error={error}>
          This action cannot be undone.
        </ConfirmationModal>
      </>
    )
  }
)
